import React from 'react';
import { HealthChallengesProps } from '../../../types/health-tools/engagement-types';
import HealthCard from '../ui/HealthCard';
import ProgressBar from '../ui/ProgressBar';
import { Trophy } from 'lucide-react';

/**
 * Health Challenges Component
 * 
 * Displays active health challenges and their progress
 * 
 * @param challenges - Array of health challenge objects
 * @param className - Additional CSS classes
 */
const HealthChallenges: React.FC<HealthChallengesProps> = ({
  challenges,
  className = '',
}) => {
  // Get challenge status color
  const getChallengeColor = (index: number) => {
    const colors = [
      'var(--brand-primary)',
      'var(--status-info)',
      'var(--status-warning)'
    ];
    return colors[index % colors.length];
  };
  
  // Get challenge status label
  const getChallengeStatusLabel = (progress: number) => {
    if (progress < 40) return 'Just Started';
    if (progress < 70) return 'On Track';
    return 'Almost There!';
  };
  
  // Calculate days left from endDate
  const getDaysLeft = (challenge: any): number => {
    // Use daysLeft if it's directly provided
    if (challenge.daysLeft !== undefined) {
      return challenge.daysLeft;
    }
    
    // Otherwise calculate from endDate
    if (challenge.endDate) {
      const end = new Date(challenge.endDate);
      const today = new Date();
      const diffTime = end.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    }
    
    return 0;
  };
  
  // Get challenge title (might be in either title or name property)
  const getChallengeTitle = (challenge: any): string => {
    return challenge.title || challenge.name || 'Unnamed Challenge';
  };
  
  return (
    <HealthCard 
      title="Health Challenges" 
      icon={Trophy}
      className={className}
    >
      <div className="space-y-4">
        {challenges.length > 0 ? (
          challenges.map((challenge, index) => (
            <div key={challenge.id} className="bg-[var(--background-surface)] rounded-lg p-3">
              <div className="flex justify-between items-center mb-2">
                <p className="font-medium text-[var(--text-primary)]">{getChallengeTitle(challenge)}</p>
                <span className="text-xs text-[var(--text-secondary)]">{getDaysLeft(challenge)} days left</span>
              </div>
              
              <ProgressBar 
                value={challenge.progress} 
                max={100} 
                color={getChallengeColor(index)} 
                className="mb-2" 
              />
              
              <div className="flex justify-between items-center">
                <span className="text-xs text-[var(--text-secondary)]">Progress: {challenge.progress}%</span>
                <span className="text-xs px-2 py-0.5 bg-[var(--brand-primary-subtle)] text-[var(--brand-primary)] rounded-full">
                  {challenge.status || getChallengeStatusLabel(challenge.progress)}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-[var(--background-surface)] rounded-lg p-4 text-center">
            <p className="text-[var(--text-secondary)]">
              You don't have any active challenges.
              Join one to start tracking your progress!
            </p>
          </div>
        )}
        
        <button className="w-full mt-2 px-4 py-2 bg-[var(--brand-primary)] text-white rounded hover:bg-[var(--brand-primary-hover)]">
          Join New Challenge
        </button>
      </div>
      
      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
        <div className="bg-[var(--background-surface)] p-2 rounded-lg">
          <p className="font-medium text-[var(--text-primary)]">3</p>
          <p className="text-[var(--text-secondary)]">Completed</p>
        </div>
        <div className="bg-[var(--background-surface)] p-2 rounded-lg">
          <p className="font-medium text-[var(--text-primary)]">{challenges.length}</p>
          <p className="text-[var(--text-secondary)]">Active</p>
        </div>
        <div className="bg-[var(--background-surface)] p-2 rounded-lg">
          <p className="font-medium text-[var(--text-primary)]">12</p>
          <p className="text-[var(--text-secondary)]">Available</p>
        </div>
      </div>
    </HealthCard>
  );
};

export default HealthChallenges; 